import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import GradientButton from '../common/GradientButton';
import Input from '../common/Input';
import JalaliDatePicker from '../common/JalaliDatePicker';
import Modal from '../common/Modal';
import Select from '../common/Select';
import { createMatchApi, updateMatchApi } from '../../api/match.api';
import { getStagesApi } from '../../api/stage.api';
import { MATCH_TYPE_LABELS, AGE_GROUP_LABELS, GENDER_LABELS } from '../../utils/constants';

const emptyForm = {
  title: '',
  type: 'FOOTBALL',
  gender: 'MALE',
  ageGroup: '',
  date: '',
  time: '',
  location: '',
  homeClubId: '',
  awayClubId: '',
  round: '',
  season: '',
  tournamentId: '',
  stageId: '',
  groupId: '',
};

export default function MatchForm({ open, onClose, onCreated, clubs, tournaments, editingMatch }) {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stages, setStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(false);

  const isEdit = Boolean(editingMatch);

  useEffect(() => {
    if (!open) return;
    if (editingMatch) {
      setForm({
        title: editingMatch.title || '',
        type: editingMatch.type || 'FOOTBALL',
        gender: editingMatch.gender || 'MALE',
        ageGroup: editingMatch.ageGroup || '',
        date: editingMatch.date ? editingMatch.date.substring(0, 10) : '',
        time: editingMatch.time || '',
        location: editingMatch.location || '',
        homeClubId: editingMatch.homeClubId ? String(editingMatch.homeClubId) : '',
        awayClubId: editingMatch.awayClubId ? String(editingMatch.awayClubId) : '',
        round: editingMatch.round || '',
        season: editingMatch.season || '',
        tournamentId: editingMatch.tournamentId ? String(editingMatch.tournamentId) : '',
        stageId: editingMatch.stageId ? String(editingMatch.stageId) : '',
        groupId: editingMatch.groupId ? String(editingMatch.groupId) : '',
      });
    } else {
      setForm(emptyForm);
    }
    setError('');
  }, [open, editingMatch]);

  useEffect(() => {
    if (!form.tournamentId) {
      setStages([]);
      return;
    }
    let mounted = true;
    setStagesLoading(true);
    getStagesApi(form.tournamentId)
      .then(({ data }) => {
        if (mounted) setStages(data.data || []);
      })
      .catch(() => {
        if (mounted) setStages([]);
      })
      .finally(() => {
        if (mounted) setStagesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [form.tournamentId]);

  if (!open) return null;

  const selectedStage = stages.find((s) => String(s.id) === String(form.stageId));
  const availableGroups = selectedStage?.groups || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tournamentId') {
      setForm({ ...form, tournamentId: value, stageId: '', groupId: '' });
      return;
    }
    if (name === 'stageId') {
      setForm({ ...form, stageId: value, groupId: '' });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleDateChange = (date) => {
    setForm({ ...form, date });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.homeClubId && form.homeClubId === form.awayClubId) {
      setError('باشگاه میزبان و مهمان نمی‌ توانند یکسان باشند');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        ageGroup: form.ageGroup || null,
        tournamentId: form.tournamentId || null,
        stageId: form.stageId || null,
        groupId: form.groupId || null,
        date: form.date,
      };

      if (isEdit) {
        await updateMatchApi(editingMatch.id, payload);
      } else {
        await createMatchApi(payload);
      }

      onCreated();
      onClose();
      setForm(emptyForm);
    } catch (err) {
      setError(err.response?.data?.message || (isEdit ? 'ویرایش مسابقه ناموفق بود' : 'ثبت مسابقه ناموفق بود'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'ویرایش مسابقه' : 'ثبت مسابقه جدید'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="عنوان مسابقه" name="title" value={form.title} onChange={handleChange} required className="col-span-2" />

          <Select
            label="باشگاه میزبان"
            required
            name="homeClubId"
            value={form.homeClubId}
            onChange={handleChange}
          >
            <option value="">باشگاه میزبان</option>
            {(clubs || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>

          <Select
            label="باشگاه مهمان"
            required
            name="awayClubId"
            value={form.awayClubId}
            onChange={handleChange}
          >
            <option value="">باشگاه مهمان</option>
            {(clubs || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>

          <JalaliDatePicker
            label="تاریخ مسابقه"
            value={form.date}
            onChange={handleDateChange}
            required
          />

          <Input label="ساعت" type="time" name="time" value={form.time} onChange={handleChange} />

          <Select
            label="نوع مسابقه"
            required
            name="type"
            value={form.type}
            onChange={handleChange}
          >
            {Object.entries(MATCH_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>

          <Select
            label="جنسیت"
            required
            name="gender"
            value={form.gender}
            onChange={handleChange}
          >
            {Object.entries(GENDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>

          <Select
            label="گروه سنی (اختیاری)"
            name="ageGroup"
            value={form.ageGroup}
            onChange={handleChange}
          >
            <option value="">گروه سنی (اختیاری)</option>
            {Object.entries(AGE_GROUP_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>

          <Select
            label="تورنمنت (اختیاری)"
            name="tournamentId"
            value={form.tournamentId}
            onChange={handleChange}
          >
            <option value="">تورنمنت (اختیاری)</option>
            {(tournaments || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>

          {form.tournamentId && (
            <>
              <Select
                label="مرحله (اختیاری)"
                name="stageId"
                value={form.stageId}
                onChange={handleChange}
                disabled={stagesLoading}
              >
                <option value="">
                  {stagesLoading ? 'در حال بارگذاری مراحل...' : 'مرحله (اختیاری)'}
                </option>
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </Select>

              {form.stageId && (
                <Select
                  label="گروه (اختیاری)"
                  name="groupId"
                  value={form.groupId}
                  onChange={handleChange}
                >
                  <option value="">گروه (اختیاری)</option>
                  {availableGroups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                  {availableGroups.length === 0 && (
                    <option value="" disabled>این مرحله گروهی ندارد</option>
                  )}
                </Select>
              )}
            </>
          )}

          <Input label="محل برگزاری" name="location" value={form.location} onChange={handleChange} required className="col-span-2" />

          <Input label="فصل" name="season" value={form.season} onChange={handleChange} required placeholder="مثلا 1404" />
          <Input label="دور (اختیاری)" name="round" value={form.round} onChange={handleChange} />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} type="button">انصراف</Button>
          <GradientButton type="submit" disabled={submitting}>
            {submitting ? 'در حال ثبت...' : isEdit ? 'ذخیره تغییرات' : 'ثبت مسابقه'}
          </GradientButton>
        </div>
      </form>
    </Modal>
  );
}
